<template>
  <div class="folder-item-container">
    <div
      class="folder-row"
      :class="{ active: activeId === item.id, 'drag-over': isDragOver }"
      :style="{ paddingLeft: level * 10 + 10 + 'px' }"
      :draggable="!readonly && item.is_base !== 1"
      @click.stop="onSelect"
      @dragstart.stop="onDragStart($event, item)"
      @dragover.prevent.stop="onDragOver"
      @dragleave.stop="onDragLeave"
      @drop.stop="onDrop($event, item)"
    >
      <div class="row-content">
        <span
          class="toggle-icon"
          @click.stop="toggleExpand"
          :style="{ visibility: hasChildren ? 'visible' : 'hidden' }"
        >
          <CaretDownOutlined v-if="isExpanded" />
          <CaretRightOutlined v-else />
        </span>
        <FolderOutlined class="folder-icon" />

        <!-- Editing Mode -->
        <div v-if="isEditing" class="edit-input-wrapper" @click.stop>
          <a-input
            ref="nameInput"
            v-model:value="editName"
            size="small"
            @blur="onRenameSubmit"
            @pressEnter="onRenameSubmit"
          />
        </div>
        <!-- Display Mode -->
        <span
          v-else
          class="folder-name"
          :title="item.name"
          @dblclick.stop="onDblClick"
          >{{ item.name }}</span
        >
      </div>
      <div
        class="row-actions"
        v-if="!readonly && item.is_base !== 1 && !isEditing"
      >
        <DeleteOutlined class="delete-icon" @click.stop="onDelete" />
      </div>
    </div>
    <div v-if="isExpanded && hasChildren" class="folder-children">
      <FolderItem
        v-for="child in item.children"
        :key="child.id"
        :item="child"
        :level="level + 1"
        :active-id="activeId"
        :editing-id="editingId"
        :readonly="readonly"
        @select="onChildSelect"
        @delete="onChildDelete"
        @rename="onChildRename"
        @edit="onChildEdit"
        @move="onChildMove"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch, nextTick } from "vue";
import {
  FolderOutlined,
  DeleteOutlined,
  CaretDownOutlined,
  CaretRightOutlined,
} from "@ant-design/icons-vue";

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  level: {
    type: Number,
    default: 0,
  },
  activeId: {
    type: [String, Number],
    default: "",
  },
  editingId: {
    type: [String, Number],
    default: "",
  },
  readonly: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["select", "delete", "rename", "edit", "move"]);

const isExpanded = ref(true);
const editName = ref("");
const nameInput = ref<any>(null);
const isDragOver = ref(false);

const isEditing = computed(() => {
  return props.item.id === props.editingId;
});

const hasChildren = computed(() => {
  return props.item.children && props.item.children.length > 0;
});

// Watch for entering edit mode to focus input
watch(
  () => isEditing.value,
  (val) => {
    if (val) {
      editName.value = props.item.name;
      nextTick(() => {
        nameInput.value?.focus();
      });
    }
  },
  { immediate: true }
);

const toggleExpand = () => {
  isExpanded.value = !isExpanded.value;
};

const onSelect = () => {
  if (!isEditing.value) {
    emit("select", props.item);
  }
};

const onDblClick = () => {
  if (!props.readonly && props.item.is_base !== 1) {
    emit("edit", props.item.id);
  }
};

const onDragStart = (event: DragEvent, item: any) => {
  if (event.dataTransfer) {
    event.dataTransfer.setData("text/plain", String(item.id));
    event.dataTransfer.effectAllowed = "move";
  }
};

const onDragOver = (event: DragEvent) => {
  isDragOver.value = true;
  if (event.dataTransfer) {
    event.dataTransfer.dropEffect = "move";
  }
};

const onDragLeave = () => {
  isDragOver.value = false;
};

const onDrop = (event: DragEvent, item: any) => {
  isDragOver.value = false;
  const draggedId = event.dataTransfer?.getData("text/plain");
  if (draggedId && String(draggedId) !== String(item.id)) {
    emit("move", { id: Number(draggedId), targetId: item.id });
  }
};

const onDelete = () => {
  emit("delete", props.item.id);
};

const onRenameSubmit = () => {
  if (editName.value && editName.value !== props.item.name) {
    emit("rename", { id: props.item.id, name: editName.value });
  } else {
    emit("rename", { id: props.item.id, name: null });
  }
};

const onChildSelect = (item: any) => {
  emit("select", item);
};

const onChildDelete = (id: any) => {
  emit("delete", id);
};

const onChildRename = (payload: any) => {
  emit("rename", payload);
};

const onChildEdit = (id: any) => {
  emit("edit", id);
};

const onChildMove = (payload: any) => {
  emit("move", payload);
};
</script>

<style scoped lang="scss">
.folder-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 10px;
  cursor: pointer;
  color: #333; /* Standard text color */
  transition: background-color 0.2s;
  font-size: 14px;
  line-height: 24px;
  border-left: 2px solid transparent; /* Prevent layout jump */

  &:hover {
    background-color: #f5f5f5; /* Light hover */

    .row-actions {
      display: block;
    }
  }

  &.active {
    background-color: #e6f7ff; /* Ant Design light blue selection */
    color: #1890ff; /* Primary blue text */
    border-left-color: #1890ff; /* Selection indicator on left */
  }

  &.drag-over {
    outline: 2px solid #1890ff;
    background-color: rgba(24, 144, 255, 0.1);
    z-index: 1; /* Ensure outline is visible */
  }

  .row-content {
    display: flex;
    align-items: center;
    flex: 1;
    overflow: hidden;

    .toggle-icon {
      margin-right: 4px;
      cursor: pointer;
      font-size: 12px;
      width: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .folder-icon {
      margin-right: 8px;
      font-size: 16px;
      color: #ffca28; /* Keep gold for folder icon, or use #1890ff for blue theme */
    }

    .folder-name {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .edit-input-wrapper {
      flex: 1;
      margin-right: 10px;
    }
  }

  .row-actions {
    display: none;
    margin-left: 10px;

    .delete-icon {
      color: #999;
      font-size: 14px;
      &:hover {
        color: #ff4d4f;
      }
    }
  }
}
</style>
